"use client"

import { useState, useEffect } from "react"
import styled from "styled-components"
import {
  format,
  addMonths,
  subMonths,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  isSameMonth,
  isSameDay,
} from "date-fns"
import { ko } from "date-fns/locale"
import { ChevronLeft, ChevronRight } from "lucide-react"

const CalendarContainer = styled.div`
  width: 256px;
  background-color: white;
  border-radius: 8px;
  padding: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`

const CalendarHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`

const MonthNavButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  background-color: transparent;
  border-radius: 4px;
  cursor: pointer;
  
  &:hover {
    background-color: #f3f4f6;
  }
`

const MonthTitle = styled.div`
  font-weight: 600;
  font-size: 16px;
`

const WeekdaysRow = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  margin-bottom: 8px;
`

const Weekday = styled.div`
  text-align: center;
  font-size: 12px;
  font-weight: 500;
  color: #6b7280;
  padding: 4px;
`

const DaysGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 2px;
`

const DayCell = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 30px;
  font-size: 13px;
  border: none;
  background-color: ${(props) => (props.isSelected ? "#2563eb" : props.isCurrentMonth ? "transparent" : "#f9fafb")};
  color: ${(props) => (props.isSelected ? "white" : props.isCurrentMonth ? "#1f2937" : "#9ca3af")};
  border-radius: 4px;
  cursor: pointer;
  
  &:hover {
    background-color: ${(props) => (props.isSelected ? "#2563eb" : "#f3f4f6")};
  }
  
  &:disabled {
    cursor: default;
    opacity: 0.5;
    background-color: transparent;
  }
`

export default function Calendar({ selected, onSelect, mode = "single" }) {
  const [currentMonth, setCurrentMonth] = useState(selected || new Date())

  useEffect(() => {
    if (selected) {
      setCurrentMonth(selected)
    }
  }, [selected])

  const prevMonth = () => {
    setCurrentMonth(subMonths(currentMonth, 1))
  }

  const nextMonth = () => {
    setCurrentMonth(addMonths(currentMonth, 1))
  }

  const handleDateClick = (day) => {
    onSelect(day)
  }

  const renderDays = () => {
    const monthStart = startOfMonth(currentMonth)
    const monthEnd = endOfMonth(monthStart)
    const startDate = startOfWeek(monthStart, { weekStartsOn: 0 })
    const endDate = endOfWeek(monthEnd, { weekStartsOn: 0 })

    const rows = []
    let days = []
    let day = startDate

    // 요일 헤더
    const weekDays = []
    for (let i = 0; i < 7; i++) {
      weekDays.push(<Weekday key={i}>{format(addDays(startDate, i), "E", { locale: ko })}</Weekday>)
    }

    // 날짜 그리드
    while (day <= endDate) {
      for (let i = 0; i < 7; i++) {
        const cloneDay = new Date(day)
        days.push(
          <DayCell
            key={day.toString()}
            isSelected={selected && isSameDay(day, selected)}
            isCurrentMonth={isSameMonth(day, monthStart)}
            onClick={() => handleDateClick(cloneDay)}
          >
            {format(day, "d")}
          </DayCell>,
        )
        day = addDays(day, 1)
      }
      rows.push(days)
      days = []
    }

    return (
      <>
        <WeekdaysRow>{weekDays}</WeekdaysRow>
        <DaysGrid>{rows.flat()}</DaysGrid>
      </>
    )
  }

  return (
    <CalendarContainer>
      <CalendarHeader>
        <MonthNavButton onClick={prevMonth}>
          <ChevronLeft size={16} />
        </MonthNavButton>
        <MonthTitle>{format(currentMonth, "yyyy년 MM월", { locale: ko })}</MonthTitle>
        <MonthNavButton onClick={nextMonth}>
          <ChevronRight size={16} />
        </MonthNavButton>
      </CalendarHeader>
      {renderDays()}
    </CalendarContainer>
  )
}

